import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { nodeTypes } from "../components/flow/FlowNodes";
import { sampleFlow } from "../data/flowMock";
import { Button } from "../components/ui/button";
import { Play, HelpCircle, Wrench, CheckCircle2, ShoppingCart, Package, Save, RotateCcw, Trash2, Plus, ChevronDown, Sparkles, FolderOpen, Folder, FilePlus2, Copy, Target, Type, Stethoscope, ClipboardCheck, ScanSearch } from "lucide-react";
import { listFlows, getActiveId, setActiveId, getFlow, saveFlow, deleteFlow, newBlankFlow, loadFlows } from "../lib/flowStorage";
import AIFlowBuilder from "../components/AIFlowBuilder";
import { PricebookProvider, usePricebook } from "../contexts/PricebookContext";
import { toast } from "sonner";

const TRADES = ["", "HVAC", "Plumbing", "Electrical", "Gas"];
const SUBCATEGORIES = ["", "Service Call", "Maintenance", "Installation", "Inspection", "Emergency"];

/**
 * Bulletproof React-Flow controls that survive the app's dark Theme Studio.
 * The default @xyflow/react Controls render tiny 2px-wide SVGs on our dark
 * theme because a global `svg { width: auto }` reset collapses them. We
 * replace them with our own dark buttons whose icons are inline SVGs sized
 * via `style` (which the theme cannot override).
 */
function CustomControls({ onToggleInteractive, interactive = true }) {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  const btnStyle = {
    width: 34,
    height: 34,
    borderRadius: 6,
    border: "1px solid #334155",
    backgroundColor: "#0f172a",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
    transition: "background-color 120ms",
    padding: 0,
  };
  const svgStyle = { width: 16, height: 16, minWidth: 16, minHeight: 16, display: "block", color: "#ffffff" };
  const hoverIn = (e) => { e.currentTarget.style.backgroundColor = "#1e293b"; e.currentTarget.style.borderColor = "#64748b"; };
  const hoverOut = (e) => { e.currentTarget.style.backgroundColor = "#0f172a"; e.currentTarget.style.borderColor = "#334155"; };

  return (
    <div
      className="react-flow__panel react-flow__controls"
      style={{ display: "flex", flexDirection: "column", gap: 4, position: "absolute", left: 12, bottom: 12, zIndex: 5 }}
      data-testid="flow-custom-controls"
    >
      <button
        type="button"
        title="Zoom in"
        data-testid="flow-zoom-in"
        onClick={() => zoomIn()}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={btnStyle}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={svgStyle} aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M11 8v6" /><path d="M8 11h6" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      </button>
      <button
        type="button"
        title="Zoom out"
        data-testid="flow-zoom-out"
        onClick={() => zoomOut()}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={btnStyle}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={svgStyle} aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M8 11h6" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      </button>
      <button
        type="button"
        title="Fit view"
        data-testid="flow-fit-view"
        onClick={() => fitView({ padding: 0.2, duration: 250 })}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={btnStyle}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={svgStyle} aria-hidden="true">
          <path d="M3 8V5a2 2 0 0 1 2-2h3" />
          <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
          <path d="M3 16v3a2 2 0 0 0 2 2h3" />
          <path d="M21 16v3a2 2 0 0 1-2 2h-3" />
        </svg>
      </button>
      <button
        type="button"
        title={interactive ? "Lock canvas (disable panning/zoom)" : "Unlock canvas"}
        data-testid="flow-lock-toggle"
        onClick={() => onToggleInteractive && onToggleInteractive()}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={btnStyle}
      >
        {interactive ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={svgStyle} aria-hidden="true">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={svgStyle} aria-hidden="true">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0" />
          </svg>
        )}
      </button>
    </div>
  );
}

const PRIORITIES = ["", "Normal", "High", "Urgent", "Low"];

const STORAGE_KEY = "servicepro_flow_v1"; // legacy, kept for back-compat reads

const paletteItems = [
  { type: "startNode", label: "Start", icon: Play, color: "#10B981", bg: "#ECFDF5" },
  { type: "questionNode", label: "Question", icon: HelpCircle, color: "#2563EB", bg: "#EFF6FF" },
  { type: "inputNode", label: "Input", icon: Type, color: "#0D9488", bg: "#F0FDFA" },
  { type: "actionNode", label: "Action", icon: Wrench, color: "#F59E0B", bg: "#FFFBEB" },
  { type: "diagnosisNode", label: "Diagnosis", icon: CheckCircle2, color: "#8B5CF6", bg: "#FAF5FF" },
  { type: "recommendProductNode", label: "Recommend Product", icon: ShoppingCart, color: "#0EA5E9", bg: "#F0F9FF" },
];

const PURPOSES = [
  { value: "diagnostic", label: "Diagnostic (Diagnostic Guru)" },
  { value: "checklist", label: "Job-completion checklist" },
];

let idCounter = 100;
const newId = () => `n${++idCounter}`;

function FlowEditor() {
  const [flows, setFlows] = useState(() => listFlows());
  const [activeFlowId, setActiveFlowId] = useState(() => getActiveId());
  const initial = (() => {
    const f = getFlow(getActiveId());
    return f || { name: "Untitled", nodes: [], edges: [] };
  })();
  const [flowName, setFlowName] = useState(initial.name);
  const [trade, setTrade] = useState(initial.trade || "");
  const [subCategory, setSubCategory] = useState(initial.subCategory || "");
  const [priority, setPriority] = useState(initial.priority || "");
  const [mandatory, setMandatory] = useState(!!initial.mandatory);
  const [purpose, setPurpose] = useState(initial.purpose || "diagnostic");
  const [equipmentType, setEquipmentType] = useState(initial.equipmentType || "");
  const [symptomKeywords, setSymptomKeywords] = useState((initial.symptomKeywords || []).join(", "));
  const [saving, setSaving] = useState(false);
  const hydratedRef = useRef(false);
  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    (initial.edges || []).map((e) => ({ ...e, markerEnd: { type: MarkerType.ArrowClosed, color: "#94A3B8" }, style: e.style || { stroke: "#94A3B8", strokeWidth: 1.6 } }))
  );
  const [selected, setSelected] = useState(null);
  const wrapperRef = useRef(null);
  const [rfInstance, setRfInstance] = useState(null);
  const [canvasInteractive, setCanvasInteractive] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");

  // Hydrate flows from the backend on mount, then load the active flow once.
  useEffect(() => {
    const onChange = () => {
      setFlows(listFlows());
    };
    window.addEventListener("flows-changed", onChange);
    window.addEventListener("active-flow-changed", onChange);
    loadFlows().then((list) => {
      setFlows(list);
      if (!hydratedRef.current && list.length) {
        hydratedRef.current = true;
        loadFlowById(getActiveId() || list[0].id);
      }
    });
    return () => {
      window.removeEventListener("flows-changed", onChange);
      window.removeEventListener("active-flow-changed", onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFlowById = useCallback((id) => {
    const f = getFlow(id);
    if (!f) return;
    setActiveFlowId(id);
    setActiveId(id);
    setFlowName(f.name);
    setTrade(f.trade || "");
    setSubCategory(f.subCategory || "");
    setPriority(f.priority || "");
    setMandatory(!!f.mandatory);
    setPurpose(f.purpose || "diagnostic");
    setEquipmentType(f.equipmentType || "");
    setSymptomKeywords((f.symptomKeywords || []).join(", "));
    setNodes(f.nodes || []);
    setEdges(
      (f.edges || []).map((e) => ({ ...e, markerEnd: { type: MarkerType.ArrowClosed, color: "#94A3B8" }, style: e.style || { stroke: "#94A3B8", strokeWidth: 1.6 } }))
    );
    setSelected(null);
  }, [setNodes, setEdges]);

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            label: params.sourceHandle || "",
            markerEnd: { type: MarkerType.ArrowClosed, color: "#94A3B8" },
            style: { stroke: "#94A3B8", strokeWidth: 1.6 },
          },
          eds
        )
      ),
    [setEdges]
  );

  const onEdgeDoubleClick = useCallback(
    (event, edge) => {
      event.stopPropagation();
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    },
    [setEdges]
  );

  const onSelectionChange = useCallback(({ nodes: sel }) => {
    setSelected(sel && sel[0] ? sel[0] : null);
  }, []);

  const onDragStart = (e, nodeType) => {
    e.dataTransfer.setData("application/reactflow", nodeType);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const type = e.dataTransfer.getData("application/reactflow");
      if (!type || !rfInstance) return;
      const position = rfInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const data =
        type === "questionNode"
          ? { label: "New question?", choices: ["Yes", "No"], required: false }
          : type === "inputNode"
            ? { label: "Enter details", placeholder: "Type answer…", required: false }
          : type === "diagnosisNode"
            ? { label: "Diagnosis", recommendation: "Recommendation details", required: false }
            : type === "actionNode"
              ? { label: "Action step", description: "Describe what to check", required: false }
              : type === "recommendProductNode"
                ? { label: "Recommended products", productIds: [] }
                : { label: "Start", description: "" };
      setNodes((nds) => [...nds, { id: newId(), type, position, data }]);
    },
    [rfInstance, setNodes]
  );

  const updateSelectedData = (patch) => {
    if (!selected) return;
    setNodes((nds) =>
      nds.map((n) => (n.id === selected.id ? { ...n, data: { ...n.data, ...patch } } : n))
    );
    setSelected((s) => (s ? { ...s, data: { ...s.data, ...patch } } : s));
  };

  const deleteSelected = () => {
    if (!selected) return;
    setNodes((nds) => nds.filter((n) => n.id !== selected.id));
    setEdges((eds) => eds.filter((e) => e.source !== selected.id && e.target !== selected.id));
    setSelected(null);
  };

  const _payload = (overrideId) => ({
    id: overrideId === null ? undefined : (overrideId || activeFlowId),
    name: flowName, nodes, edges, trade, subCategory, priority, mandatory, purpose,
    equipmentType,
    symptomKeywords: symptomKeywords.split(",").map((k) => k.trim()).filter(Boolean),
  });

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveFlow(_payload());
      setActiveFlowId(saved.id);
      setFlows(listFlows());
      toast.success("Flow saved");
    } catch (e) {
      toast.error("Failed to save flow");
    } finally {
      setSaving(false);
    }
  };

  const openSaveAs = () => {
    setSaveAsName(`${flowName} (copy)`);
    setSaveAsOpen(true);
  };

  const confirmSaveAs = async () => {
    const name = (saveAsName || "").trim();
    if (!name) return;
    setSaving(true);
    try {
      const saved = await saveFlow({ ...(_payload(null)), name });
      setFlows(listFlows());
      loadFlowById(saved.id);
      setSaveAsOpen(false);
      toast.success("Saved as new flow");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleNewFlow = async () => {
    const created = await newBlankFlow("New Flow");
    setFlows(listFlows());
    loadFlowById(created.id);
  };

  const handleDeleteFlow = async (id) => {
    if (!window.confirm("Delete this flow? This cannot be undone.")) return;
    await deleteFlow(id);
    const remaining = listFlows();
    setFlows(remaining);
    const next = getActiveId();
    if (next) loadFlowById(next);
  };

  const resetSample = () => {
    setFlowName(sampleFlow.name);
    setNodes(sampleFlow.nodes);
    setEdges(
      sampleFlow.edges.map((e) => ({
        ...e,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#94A3B8" },
        style: { stroke: "#94A3B8", strokeWidth: 1.6 },
      }))
    );
    setSelected(null);
  };

  usePageHeader({
    title: "Flow Chart Builder",
    subtitle: "Design diagnostic decision trees for Diagnostic Guru",
    actions: (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          className="h-9 text-[13px] border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
          onClick={() => setAiOpen(true)}
        >
          <Sparkles className="h-4 w-4 mr-1.5" /> Build with AI
        </Button>
        <Button variant="outline" className="h-9 text-[13px]" onClick={handleNewFlow}>
          <FilePlus2 className="h-4 w-4 mr-1.5" /> New
        </Button>
        <Button variant="outline" className="h-9 text-[13px]" onClick={openSaveAs} data-testid="flow-save-as-btn">
          <Copy className="h-4 w-4 mr-1.5" /> Save As
        </Button>
        <Button className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px]" onClick={save} disabled={saving} data-testid="flow-save-btn">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    ),
  });

  return (
    <>
      <AIFlowBuilder
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onSaved={(saved) => {
          setFlows(listFlows());
          setTimeout(() => loadFlowById(saved.id), 200);
        }}
      />
      <div className="p-6 pb-0">
        <input
          type="text"
          value={flowName}
          onChange={(e) => setFlowName(e.target.value)}
          className="text-[15px] font-semibold text-slate-900 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-400 focus:outline-none px-1 py-0.5 transition-colors"
        />
      </div>
      <div className="flex-1 p-6 pt-3">
        <div className="h-[calc(100vh-180px)] grid grid-cols-12 gap-4">
          {/* Left column: My Flows + Palette */}
          <div className="col-span-2 flex flex-col gap-3 min-h-0">
            {/* My Flows */}
            <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col min-h-0 max-h-[40%]">
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5">
                  <FolderOpen className="h-3 w-3" /> My Flows ({flows.length})
                </div>
                <button
                  onClick={handleNewFlow}
                  className="h-6 w-6 rounded hover:bg-slate-100 text-slate-500 flex items-center justify-center"
                  title="New flow"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-1 -mx-1 px-1">
                {flows.map((f) => {
                  const active = f.id === activeFlowId;
                  return (
                    <div
                      key={f.id}
                      onClick={() => loadFlowById(f.id)}
                      className={`group cursor-pointer rounded-md px-2 py-1.5 flex items-center gap-1.5 transition-colors ${
                        active ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-50 border border-transparent"
                      }`}
                    >
                      <Folder className={`h-3.5 w-3.5 shrink-0 ${active ? "text-blue-600" : "text-slate-400"}`} />
                      <div className="min-w-0 flex-1">
                        <div className={`text-[12px] font-medium truncate ${active ? "text-blue-900" : "text-slate-800"}`}>
                          {f.name}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {(f.nodes || []).length}n · {(f.edges || []).length}e
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFlow(f.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded hover:bg-red-50 text-red-500 flex items-center justify-center transition-all"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Palette */}
            <div className="bg-white rounded-xl border border-slate-200 p-3 flex-1 min-h-0 overflow-y-auto">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 px-1">Nodes</div>
              <div className="space-y-2">
                {paletteItems.map((p) => {
                  const Icon = p.icon;
                  return (
                    <div
                      key={p.type}
                      draggable
                      onDragStart={(e) => onDragStart(e, p.type)}
                      className="cursor-grab active:cursor-grabbing flex items-center gap-2 p-2 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all bg-white"
                    >
                    <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0" style={{ background: p.bg }}>
                      <Icon className="h-4 w-4" style={{ color: p.color }} />
                    </div>
                    <div>
                      <div className="text-[12.5px] font-semibold text-slate-800">{p.label}</div>
                      <div className="text-[10.5px] text-slate-500">Drag to canvas</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 px-1">Stats</div>
              <div className="text-[12px] text-slate-600 space-y-1 px-1">
                <div>Nodes: <span className="font-semibold text-slate-900">{nodes.length}</span></div>
                <div>Connections: <span className="font-semibold text-slate-900">{edges.length}</span></div>
              </div>

              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 px-1 flex items-center gap-1.5">
                  <ClipboardCheck className="h-3 w-3" /> Flow purpose
                </div>
                <div className="px-1 mb-3">
                  <select
                    data-testid="flow-purpose"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    className="w-full text-[12.5px] rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  >
                    {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                  <p className="text-[10.5px] text-slate-500 mt-1 leading-snug">
                    {purpose === "checklist"
                      ? "Runs as a step-by-step checklist when a tech completes a matching job. Tick \"Required\" on the questions that must be answered to close the job."
                      : "Used by Guru to diagnose problems and recommend products."}
                  </p>
                </div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 px-1 flex items-center gap-1.5">
                  <Target className="h-3 w-3" /> Applies to job type
                </div>
                <div className="space-y-2 px-1" data-testid="flow-targeting">
                  <TargetSelect label="Trade" value={trade} onChange={setTrade} options={TRADES} testId="flow-trade" />
                  <TargetSelect label="Sub-category" value={subCategory} onChange={setSubCategory} options={SUBCATEGORIES} testId="flow-subcategory" />
                  <TargetSelect label="Priority" value={priority} onChange={setPriority} options={PRIORITIES} testId="flow-priority" />
                  <label className="flex items-center gap-2 mt-1 cursor-pointer select-none" data-testid="flow-mandatory-toggle">
                    <input
                      type="checkbox"
                      checked={mandatory}
                      onChange={(e) => setMandatory(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-[12px] font-medium text-slate-700">Make this flow mandatory</span>
                  </label>
                  {mandatory && (
                    <p className="text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 leading-snug">
                      Technicians must complete this checklist before they can close a matching job.
                    </p>
                  )}
                </div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 mt-4 px-1 flex items-center gap-1.5">
                  <ScanSearch className="h-3 w-3" /> Free Chat matching
                </div>
                <div className="space-y-2 px-1">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600">Equipment type</label>
                    <input
                      value={equipmentType}
                      onChange={(e) => setEquipmentType(e.target.value)}
                      placeholder="e.g. Split AC, Tankless Water Heater"
                      data-testid="flow-equipment-type"
                      className="w-full text-[12.5px] rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/30 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600">Symptom keywords (comma-separated)</label>
                    <input
                      value={symptomKeywords}
                      onChange={(e) => setSymptomKeywords(e.target.value)}
                      placeholder="e.g. no cooling, warm air, ac not cold"
                      data-testid="flow-symptom-keywords"
                      className="w-full text-[12.5px] rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/30 mt-1"
                    />
                  </div>
                  <p className="text-[10.5px] text-slate-500 leading-snug">
                    When a technician describes a matching symptom in Diagnostic Guru's Free Chat, this flow will be suggested as a guided procedure.
                  </p>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Canvas */}
          <div className="col-span-7 flex flex-col gap-2.5 min-h-0">
            <FlowInstructions purpose={purpose} />
            <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-hidden relative min-h-0 flow-canvas-scope" ref={wrapperRef}>
            {/* Theme-proof overrides for react-flow's Controls buttons + MiniMap.
                The app's dark theme was washing out button icons and the minimap
                to plain-white boxes; scoping these styles under
                `.flow-canvas-scope` with high specificity forces dark buttons
                with white icons and a readable minimap regardless of theme. */}
            <style>{`
              .flow-canvas-scope .react-flow__controls {
                background: transparent !important;
                box-shadow: none !important;
                border: 0 !important;
                display: flex !important;
                flex-direction: column !important;
                gap: 4px !important;
              }
              .flow-canvas-scope .react-flow__controls-button {
                background-color: #0f172a !important;
                border: 1px solid #334155 !important;
                border-radius: 6px !important;
                width: 30px !important;
                height: 30px !important;
                min-width: 30px !important;
                min-height: 30px !important;
                box-shadow: 0 1px 3px rgba(0,0,0,0.4) !important;
                color: #ffffff !important;
                fill: #ffffff !important;
              }
              .flow-canvas-scope .react-flow__controls-button:hover {
                background-color: #1e293b !important;
                border-color: #64748b !important;
              }
              .flow-canvas-scope .react-flow__controls-button svg {
                fill: #ffffff !important;
                color: #ffffff !important;
                width: 14px !important;
                height: 14px !important;
                max-width: 14px !important;
                max-height: 14px !important;
              }
              .flow-canvas-scope .react-flow__controls-button svg path,
              .flow-canvas-scope .react-flow__controls-button svg polyline,
              .flow-canvas-scope .react-flow__controls-button svg circle,
              .flow-canvas-scope .react-flow__controls-button svg rect {
                fill: #ffffff !important;
                stroke: #ffffff !important;
              }
              .flow-canvas-scope .react-flow__minimap {
                background: #0f172a !important;
                border: 1px solid #334155 !important;
                border-radius: 8px !important;
                box-shadow: 0 4px 12px rgba(0,0,0,0.35) !important;
              }
              .flow-canvas-scope .react-flow__minimap-mask {
                fill: rgba(15, 23, 42, 0.55) !important;
                stroke: #60a5fa !important;
                stroke-width: 2 !important;
              }
              .flow-canvas-scope .react-flow__minimap-node {
                fill: #60a5fa !important;
                stroke: #1e293b !important;
                stroke-width: 1 !important;
              }
            `}</style>
            {/* Tip banner — solid dark pill with white text so no theme cascade
                can hide it (this was previously white-on-white on dark themes). */}
            <div
              className="absolute top-3 left-3 z-10 rounded-md px-3 py-1.5 text-[11.5px] shadow-md pointer-events-none flex items-center gap-1.5"
              style={{
                backgroundColor: "rgba(15, 23, 42, 0.92)",
                border: "1px solid rgba(148, 163, 184, 0.5)",
                color: "#ffffff",
              }}
              data-testid="flow-canvas-tip"
            >
              <span style={{ fontWeight: 700, color: "#93c5fd" }}>Tip:</span>
              <span style={{ color: "#ffffff" }}>Drag from a handle to connect · Double-click an arrow to delete it</span>
            </div>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onEdgeDoubleClick={onEdgeDoubleClick}
              onSelectionChange={onSelectionChange}
              onInit={setRfInstance}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              nodesDraggable={canvasInteractive}
              nodesConnectable={canvasInteractive}
              elementsSelectable={canvasInteractive}
              panOnDrag={canvasInteractive}
              zoomOnScroll={canvasInteractive}
              zoomOnPinch={canvasInteractive}
              zoomOnDoubleClick={canvasInteractive}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed, color: "#94A3B8" } }}
              proOptions={{ hideAttribution: true }}
              edgesFocusable
            >
              <Background color="#CBD5E1" gap={18} size={1.2} />
              <CustomControls
                interactive={canvasInteractive}
                onToggleInteractive={() => setCanvasInteractive((v) => !v)}
              />
              <MiniMap
                pannable
                zoomable
                nodeColor="#60a5fa"
                nodeStrokeColor="#1e293b"
                nodeStrokeWidth={1}
                maskColor="rgba(15, 23, 42, 0.55)"
                bgColor="#0f172a"
              />
            </ReactFlow>
            </div>
          </div>

          {/* Inspector */}
          <div className="col-span-3 bg-white rounded-xl border border-slate-200 p-4 overflow-y-auto">
            {!selected ? (
              <div className="text-center py-10">
                <div className="h-12 w-12 mx-auto rounded-xl bg-slate-100 flex items-center justify-center mb-2">
                  <Plus className="h-5 w-5 text-slate-400" />
                </div>
                <div className="text-[13px] font-semibold text-slate-800">No node selected</div>
                <p className="text-[12px] text-slate-500 mt-1">Click any node to edit it, or drag a new node from the palette.</p>
              </div>
            ) : (
              <NodeInspector node={selected} update={updateSelectedData} onDelete={deleteSelected} />
            )}
          </div>
        </div>
      </div>

      {saveAsOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setSaveAsOpen(false)}
          data-testid="flow-save-as-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-white border border-slate-200 shadow-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <Copy className="h-4 w-4 text-blue-600" />
              <div className="text-[15px] font-bold text-slate-900">Save as a new flow</div>
            </div>
            <p className="text-[12.5px] text-slate-500 mb-3">Creates a copy with the current nodes, edges and settings.</p>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">New flow name</label>
            <input
              autoFocus
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmSaveAs(); }}
              data-testid="flow-save-as-input"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              placeholder="e.g. HVAC Cooling Checklist"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setSaveAsOpen(false)}
                className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 text-[13px] font-semibold hover:bg-slate-50"
                data-testid="flow-save-as-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmSaveAs}
                disabled={saving || !saveAsName.trim()}
                className="flex-1 h-10 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-60"
                data-testid="flow-save-as-confirm"
              >
                {saving ? "Saving…" : "Save copy"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Compact, collapsible "how to build" guide shown above the canvas.
function FlowInstructions({ purpose }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shrink-0" data-testid="flow-instructions">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5"
        data-testid="flow-instructions-toggle"
      >
        <div className="text-[12.5px] font-bold text-slate-800 flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-blue-600" /> How to build this flow
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3.5 grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <div className={`rounded-lg border p-3 ${purpose === "diagnostic" ? "border-violet-300 bg-violet-50/60" : "border-slate-200"}`}>
            <div className="text-[12px] font-bold text-violet-700 flex items-center gap-1.5 mb-1">
              <Stethoscope className="h-3.5 w-3.5" /> Diagnostic (Guru)
            </div>
            <p className="text-[11.5px] text-slate-600 leading-relaxed">
              Set <b>Flow purpose → Diagnostic</b>. Start with a <b>Start</b> node, then branch with
              <b> Question</b> nodes (each answer is a connection point) into <b>Diagnosis</b> and
              <b> Recommend Product</b> nodes. Guru walks the technician through it to find the problem and suggest upsells.
            </p>
          </div>
          <div className={`rounded-lg border p-3 ${purpose === "checklist" ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200"}`}>
            <div className="text-[12px] font-bold text-emerald-700 flex items-center gap-1.5 mb-1">
              <ClipboardCheck className="h-3.5 w-3.5" /> Job-completion checklist
            </div>
            <p className="text-[11.5px] text-slate-600 leading-relaxed">
              Set <b>Flow purpose → Job-completion checklist</b>. Chain <b>Question</b> and <b>Input</b> (typed answer)
              nodes from the <b>Start</b> node and tick <b>“Required to close the job”</b> on must-answer steps. When a tech
              completes a matching job they step through it and can only <b>Close Job</b> after every required step is answered.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function NodeInspector({ node, update, onDelete }) {
  const { type, data } = node;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Editing</div>
          <div className="text-[14px] font-semibold text-slate-900 capitalize">{type.replace("Node", "")} Node</div>
        </div>
        <button onClick={onDelete} className="h-8 w-8 rounded-md hover:bg-red-50 text-red-500 flex items-center justify-center transition-colors">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <Field label={type === "inputNode" ? "Write question" : "Label"}>
        <input
          value={data.label || ""}
          onChange={(e) => update({ label: e.target.value })}
          className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        />
      </Field>

      {(type === "startNode" || type === "actionNode") && (
        <Field label="Description">
          <textarea
            value={data.description || ""}
            onChange={(e) => update({ description: e.target.value })}
            rows={3}
            className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none"
          />
        </Field>
      )}

      {type === "questionNode" && (
        <Field label={`Answer Choices (max 4) — ${(data.choices || []).length}/4`}>
          <div className="space-y-1.5">
            {(data.choices || []).map((c, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="text-[11px] font-semibold text-slate-400 w-4">{i + 1}.</span>
                <input
                  value={c}
                  onChange={(e) => {
                    const next = [...(data.choices || [])];
                    next[i] = e.target.value;
                    update({ choices: next });
                  }}
                  className="flex-1 h-8 px-2 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  placeholder={`Choice ${i + 1}`}
                />
                <button
                  onClick={() => {
                    const next = (data.choices || []).filter((_, idx) => idx !== i);
                    update({ choices: next.length ? next : ["Yes"] });
                  }}
                  className="h-8 w-8 rounded-md hover:bg-red-50 text-red-500 flex items-center justify-center transition-colors"
                  title="Remove choice"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {(data.choices || []).length < 4 && (
              <button
                onClick={() => update({ choices: [...(data.choices || []), ""] })}
                className="w-full h-8 rounded-md border border-dashed border-slate-300 text-[12px] font-medium text-slate-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-1 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add Choice
              </button>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">Each choice becomes a connection point on the node.</div>
        </Field>
      )}

      {type === "inputNode" && (
        <Field label="Input placeholder">
          <input
            value={data.placeholder || ""}
            onChange={(e) => update({ placeholder: e.target.value })}
            placeholder="e.g., Enter serial number"
            className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          />
          <div className="text-[11px] text-slate-500 mt-1.5">The technician types a free-text answer that's saved to the checklist.</div>
        </Field>
      )}

      {type === "diagnosisNode" && (
        <Field label="Recommendation">
          <textarea
            value={data.recommendation || ""}
            onChange={(e) => update({ recommendation: e.target.value })}
            rows={5}
            className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none"
          />
        </Field>
      )}

      {type === "recommendProductNode" && (
        <Field label={`Products from Pricebook (max 8) — ${(data.productIds || []).length}/8`}>
          <div className="space-y-1.5">
            {(data.productIds || []).map((pid, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="text-[11px] font-semibold text-slate-400 w-4">{i + 1}.</span>
                <ProductDropdown
                  value={pid}
                  excludeIds={(data.productIds || []).filter((_, idx) => idx !== i)}
                  onChange={(val) => {
                    const next = [...(data.productIds || [])];
                    next[i] = val;
                    update({ productIds: next });
                  }}
                />
                <button
                  onClick={() => {
                    const next = (data.productIds || []).filter((_, idx) => idx !== i);
                    update({ productIds: next });
                  }}
                  className="h-8 w-8 rounded-md hover:bg-red-50 text-red-500 flex items-center justify-center transition-colors shrink-0"
                  title="Remove product"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {(data.productIds || []).length < 8 && (
              <button
                onClick={() => update({ productIds: [...(data.productIds || []), ""] })}
                className="w-full h-8 rounded-md border border-dashed border-slate-300 text-[12px] font-medium text-slate-600 hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50 flex items-center justify-center gap-1 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add Product
              </button>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            Each product links to a real Pricebook item and becomes a connection point.
          </div>
        </Field>
      )}
      {["questionNode", "inputNode", "diagnosisNode", "actionNode"].includes(type) && (
        <label className="flex items-center gap-2 mt-1 pt-3 border-t border-slate-100 cursor-pointer select-none" data-testid="node-required-toggle">
          <input
            type="checkbox"
            checked={!!data.required}
            onChange={(e) => update({ required: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
          />
          <span className="text-[12px] font-medium text-slate-700">Required to close the job</span>
        </label>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function TargetSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[10.5px] font-medium text-slate-500 mb-0.5">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full h-8 px-2 rounded-md border border-slate-200 text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
      >
        {options.map((o) => (
          <option key={o || "any"} value={o}>{o || "Any"}</option>
        ))}
      </select>
    </div>
  );
}

function ProductDropdown({ value, excludeIds = [], onChange }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const ref = React.useRef(null);
  const { items: pricebookItems, loading: pricebookLoading } = usePricebook();

  React.useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selected = pricebookItems.find((p) => p.id === value);
  const filtered = pricebookItems
    .filter((p) => !excludeIds.includes(p.id))
    .filter((p) => {
      const q = search.toLowerCase();
      return !q || p.name.toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q);
    });

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-8 px-2 rounded-md border border-slate-200 text-[12.5px] flex items-center justify-between gap-1 hover:border-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 bg-white"
      >
        {selected ? (
          <span className="flex items-center gap-1 min-w-0">
            <Package className="h-3 w-3 text-sky-600 shrink-0" />
            <span className="truncate text-slate-800">{selected.name}</span>
          </span>
        ) : value ? (
          <span className="text-amber-600 truncate">Item not in Pricebook ({value})</span>
        ) : (
          <span className="text-slate-400">{pricebookLoading ? "Loading pricebook…" : "Select a product…"}</span>
        )}
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-1.5 border-b border-slate-100">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, SKU, category…"
              className="w-full h-7 px-2 text-[12px] rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-[12px] text-slate-400 text-center">
                {pricebookLoading ? "Loading pricebook…" : "No Pricebook items match"}
              </div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    onChange(p.id);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={`w-full text-left px-2.5 py-1.5 hover:bg-sky-50 flex items-center gap-2 ${
                    value === p.id ? "bg-sky-50" : ""
                  }`}
                >
                  <Package className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-medium text-slate-800 truncate">{p.name}</div>
                    <div className="text-[10.5px] text-slate-500 flex items-center gap-1">
                      {p.sku && <span className="font-mono">{p.sku}</span>}
                      {p.sku && <span>·</span>}
                      <span>{p.category || (p.kind === "material" ? "Material" : "Service")}</span>
                      <span>·</span>
                      <span className="font-semibold text-emerald-700">${Number(p.unitPrice || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FlowChart() {
  return (
    <PricebookProvider>
      <ReactFlowProvider>
        <FlowEditor />
      </ReactFlowProvider>
    </PricebookProvider>
  );
}
